import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ulid } from 'ulid';

import { useDestinations } from '@/features/destinations/api/getDestinations';
import type { Destination } from '@/features/destinations/utils/Destination';
import useInBranchPreselection from '@/hooks/useInBranchPreselection';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import useThemeStore from '@/store/ThemeStore';
import { FlowType } from '@/types/FlowType';
import useAuthUserInfo from './useAuthUserInfo';

const useLandingPageSetup = (flow: FlowType) => {
  const { setFlowType, setSessionId, setSessionSource } = useSessionStore();
  const { setDestinations, selectedDestination } = useAppointmentStore();
  const { isLuxuryTheme } = useThemeStore();

  const destinationsQuery = useDestinations(isLuxuryTheme);
  useAuthUserInfo();

  const navigate = useNavigate();

  useInBranchPreselection();

  useEffect(() => {
    if (flow === FlowType.EMAIL) {
      setFlowType('emailInquiry');
    }
  }, [flow, setFlowType]);

  useEffect(() => {
    if (destinationsQuery.isSuccess) {
      const newSessionId = ulid();
      setSessionId(newSessionId);
      setSessionSource('new');
    }
  }, [destinationsQuery.isSuccess, setSessionId, setSessionSource]);

  useEffect(() => {
    if (destinationsQuery.data?.length) {
      setDestinations(destinationsQuery.data);
    }
  }, [destinationsQuery.data, setDestinations]);

  const handleClickContinue = useCallback(
    (destination?: Destination) => {
      const workTypeId =
        destination?.workTypeId || selectedDestination.workTypeId;

      if (workTypeId) {
        if (flow === FlowType.EMAIL) {
          navigate(`/email-inquiry/${workTypeId}/contact-info`);
        } else {
          navigate(`/schedule-appointment/${workTypeId}`);
        }
      }
    },
    [flow, navigate, selectedDestination.workTypeId],
  );

  return {
    handleClickContinue,
    destinationsQuery,
  };
};

export default useLandingPageSetup;
